import { all, takeEvery, put, fork, call, takeLatest } from 'redux-saga/effects';
import { push, replace } from 'connected-react-router';
import jwtDecode from 'jwt-decode';

import { getToken } from '@helpers/utility';
import kc, { signOut, getUserProfile, loadKc } from '@helpers/keycloak';
import HSBBClient from '@helpers/apiClient';
import {
  clearToken,
  startListener,
  stopListener,
  clearKcCallback,
  checkIsTokenExpired,
  updateSessionTimeout
} from '@helpers/utility';

import actions from './actions';

export function* parseKc() {
  try {
    const userProfile = yield call(getUserProfile);

    yield put({
      type: actions.LOGIN_SUCCESS,
      accessToken: kc.token,
      profile: userProfile
    });
  } catch (error) {
    yield put({
      type: actions.LOGIN_ERROR,
      error
    });
  }
}

export function* handleAuthenticationCallback() {
  yield takeLatest(actions.HANDLE_AUTHENTICATION_CALLBACK, parseKc);
}

export function* login() {
  yield takeEvery(actions.LOGIN, function* () {
    const token = getToken();
    const accessToken = token.get('accessToken');

    if (accessToken) {
      yield put(actions.checkAuthorization());
      return;
    }

    yield call(loadKc);
    kc.login({
      redirectUri: process.env.REACT_APP_KEYCLOAK_CALLBACK_URL
    });
  });
}

export function* loginSuccess() {
  yield takeEvery(actions.LOGIN_SUCCESS, function* (payload) {
    yield call(loadKc);

    const userSetting = yield call(HSBBClient.getUserSetting);
    const profile = payload.profile;

    yield put({
      type: actions.USER_PROFILE_LOADED,
      profile,
      userSetting
    });

    startListener();

    if (!payload.noRedirect) {
      yield put(push('/admin'));
    }
  });
}

export function* loginError() {
  yield takeEvery(actions.LOGIN_ERROR, function (payload) {
    stopListener();
    clearKcCallback();
    clearToken();
    console.log(payload.error);
  });
}

export function* logout() {
  yield takeEvery(actions.LOGOUT, function* () {
    stopListener();
    clearToken();

    yield call(signOut);
  });
}

export function* redirect() {
  yield takeEvery(actions.REDIRECT, function* (payload) {
    yield put(replace(payload.link));
  });
}

export function* redirectToLogin() {
  yield takeEvery(actions.REDIRECT_TO_LOGIN, function* () {
    stopListener();
    clearToken();
    yield put(actions.redirect(process.env.REACT_APP_BASENAME));
  });
}

export function* checkAuthorization() {
  yield takeEvery(actions.CHECK_AUTHORIZATION, function* () {
    try {
      const token = getToken();
      const accessToken = token.get('accessToken');
      const idToken = token.get('idToken');
      const refreshToken = token.get('refreshToken');

      if (!accessToken) {
        yield put(actions.redirectToLogin());
        return;
      }

      const sessionTimeout = window.localStorage.getItem('session_timeout');
      const now = new Date().getTime() / 1000;

      const isActive = !!sessionTimeout && sessionTimeout > now;
      const isAccessExpired = checkIsTokenExpired(accessToken);
      const isRefreshExpired = checkIsTokenExpired(refreshToken);
      const decodedAccessToken = jwtDecode(accessToken);

      const notValid =
        decodedAccessToken.iss !==
        process.env.REACT_APP_KEYCLOAK_AUTH_SERVER_URL + '/realms/' + process.env.REACT_APP_KEYCLOAK_REALM;

      if (notValid || isRefreshExpired) {
        yield put(actions.redirectToLogin());
        return;
      }

      if (isAccessExpired && !isActive) {
        yield put(actions.redirectToLogin());
        return;
      }

      if (isActive) {
        updateSessionTimeout();
      }

      yield call(loadKc);
      const profile = jwtDecode(idToken);

      yield put({
        type: actions.LOGIN_SUCCESS,
        accessToken,
        profile,
        appStatus: 'ready',
        noRedirect: true
      });
    } catch (e) {
      yield put(actions.redirectToLogin());
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(checkAuthorization),
    fork(handleAuthenticationCallback),
    fork(loginSuccess),
    fork(loginError),
    fork(login),
    fork(logout),
    fork(redirect),
    fork(redirectToLogin)
  ]);
}
